import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionProvider, PAGES } from './contexts/PermissionContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import Home from './pages/Home';
import Reports from './pages/Reports';
// TagManagement 頁面已移除 - 功能整合到設定頁的權限標籤管理 Tab
// Monitoring 頁面已移除 - 功能整合到設定頁的系統紀錄及專案監控 Tab
import Settings from './pages/Settings';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import ProjectForm from './pages/ProjectForm';
import ProjectComparison from './pages/ProjectComparison';
// ProjectTemplates 已移除 - 使用 5 步驟精靈建立專案

// 建立 QueryClient 實例
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 分鐘
      cacheTime: 10 * 60 * 1000, // 10 分鐘
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * 主要佈局元件（含 Navbar）
 */
const MainLayout = ({ children }) => (
  <div className="min-h-screen bg-purple-50">
    <Navbar />
    {children}
  </div>
);

/**
 * 受保護的佈局（需登入 + 頁面權限）
 */
const ProtectedLayout = ({ children, page = null }) => (
  <ProtectedRoute page={page}>
    <MainLayout>{children}</MainLayout>
  </ProtectedRoute>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PermissionProvider>
          <Router>
            <Toaster position="top-right" />
            <Routes>
              {/* 公開路由：登入頁 */}
              <Route path="/login" element={<Login />} />

              {/* 首頁 - 所有登入用戶可見 */}
              <Route
                path="/"
                element={
                  <ProtectedLayout>
                    <Home />
                  </ProtectedLayout>
                }
              />

              {/* 專案管理 - 需要 projects 頁面權限 */}
              <Route
                path="/projects"
                element={
                  <ProtectedLayout page={PAGES.PROJECTS}>
                    <ProjectList />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/projects/compare"
                element={
                  <ProtectedLayout page={PAGES.PROJECTS}>
                    <ProjectComparison />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/projects/:id"
                element={
                  <ProtectedLayout page={PAGES.PROJECTS}>
                    <ProjectDetail />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/projects/new"
                element={
                  <ProtectedLayout page={PAGES.PROJECTS}>
                    <ProjectForm />
                  </ProtectedLayout>
                }
              />
              {/* ProjectTemplates 路由已移除 - 使用 5 步驟精靈建立專案 */}
              <Route
                path="/projects/:id/edit"
                element={
                  <ProtectedLayout page={PAGES.PROJECTS}>
                    <ProjectForm />
                  </ProtectedLayout>
                }
              />

              {/* 年度供應商資料（原雲端報表庫）- 需要 reports 頁面權限 */}
              <Route
                path="/reports"
                element={
                  <ProtectedLayout page={PAGES.REPORTS}>
                    <Reports />
                  </ProtectedLayout>
                }
              />

              {/* 舊的 /vendors 路由重導向到 /reports */}
              <Route
                path="/vendors"
                element={<Navigate to="/reports" replace />}
              />

              {/* 標籤管理路由已移除 - 重導向到設定頁 */}
              <Route
                path="/tags"
                element={<Navigate to="/settings" replace />}
              />

              {/* 系統監控路由已移除 - 重導向到設定頁 */}
              <Route
                path="/monitoring"
                element={<Navigate to="/settings" replace />}
              />

              {/* 設定 - 需要 settings 頁面權限 */}
              <Route
                path="/settings"
                element={
                  <ProtectedLayout page={PAGES.SETTINGS}>
                    <Settings />
                  </ProtectedLayout>
                }
              />

              {/* 舊的 /users 路由重導向到 /settings */}
              <Route
                path="/users"
                element={<Navigate to="/settings" replace />}
              />

              {/* 404 頁面 */}
              <Route
                path="*"
                element={
                  <MainLayout>
                    <div className="container mx-auto px-4 py-16 text-center">
                      <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
                      <p className="text-gray-600 mb-8">找不到此頁面</p>
                      <a
                        href="/"
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                      >
                        回首頁
                      </a>
                    </div>
                  </MainLayout>
                }
              />
            </Routes>
          </Router>
        </PermissionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
